/**
 * Gera um sessionID único usando UUID v4
 */
export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Classe para gerenciar sessionID com localStorage
 */
export class SessionStorage {
  private static readonly STORAGE_KEY = 'chat_widget_session_id';
  private static readonly STORAGE_TIMESTAMP_KEY = 'chat_widget_session_timestamp';

  /**
   * Obtém ou cria um sessionID
   */
  static getOrCreateSessionId(generateFn?: () => string): string {
    const existingSession = this.getSessionId();

    if (existingSession) {
      return existingSession;
    }

    const newSessionId = generateFn ? generateFn() : generateUUID();
    this.setSessionId(newSessionId);
    return newSessionId;
  }

  /**
   * Obtém sessionID do storage
   */
  static getSessionId(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch {
      console.warn('localStorage não disponível');
      return null;
    }
  }

  /**
   * Define sessionID no storage
   */
  static setSessionId(sessionId: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, sessionId);
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.warn('Falha ao salvar session ID:', error);
    }
  }

  /**
   * Remove sessionID do storage
   */
  static clearSession(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      console.warn('Falha ao limpar sessão:', error);
    }
  }

  /**
   * Verifica se a sessão expirou (24 horas)
   */
  static isSessionExpired(maxAgeHours: number = 24): boolean {
    try {
      const timestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);
      if (!timestamp) return true;

      const sessionAge = Date.now() - parseInt(timestamp, 10);
      const maxAge = maxAgeHours * 60 * 60 * 1000;
      
      return sessionAge > maxAge;
    } catch {
      return true;
    }
  }

  /**
   * Reseta a sessão criando um novo ID
   */
  static resetSession(generateFn?: () => string): string {
    this.clearSession();
    return this.getOrCreateSessionId(generateFn);
  }

  /**
   * Cria uma nova sessão sem verificar o localStorage
   * Usado quando loadPreviousSession é false
   */
  static createNewSessionId(generateFn?: () => string): string {
    const newSessionId = generateFn ? generateFn() : generateUUID();
    return newSessionId;
  }
}
